import React, { useCallback, useEffect, useState } from 'react'

import { formatMoney, regexPhoneNumber } from 'helpers/helpers'

import ChequeLogsModal from 'pages/admin/sql/ChequeLogsModal'
import { formatPosDate, prettyJson, renderCell } from 'pages/admin/sql/format'
import { CHEQUE_STATUS_STEP_NAMES, chequeDetailQuery } from 'pages/admin/sql/queries'

const EMPTY_DETAIL = { 'products': [], 'payments': [], 'statuses': [], 'logs': [], 'error': null }

function hasValue(value) {
	return value !== null && value !== undefined && String(value).trim() !== ''
}

/** jsonb с чужой кассы может прийти и объектом, и строкой. */
function parseJson(value) {
	if (!value) return null
	if (typeof value === 'object') return value

	try {
		return JSON.parse(value)
	} catch (error) {
		return null
	}
}

function sortById(list) {
	if (!Array.isArray(list)) return []

	return [...list].sort((a, b) => Number(a?.id ?? 0) - Number(b?.id ?? 0))
}

function Row({ label, value }) {
	if (!hasValue(value)) return null

	return (
		<div className="d-flex gap-1">
			<b>{label}:</b>
			<span>{value}</span>
		</div>
	)
}

/**
 * Правая панель — тот же чек, что кассир видит справа от списка.
 *
 * История статусов здесь важнее прочего: строку в неё касса пишет, когда
 * применяет пришедшую из облака команду. Команда в ленте событий есть, а
 * записи тут нет — значит до кассы не дошло.
 */
function ChequePanel({ monitor, schema, cheque, refreshToken }) {
	const query = monitor.query

	// Загрузка привязана к идентификаторам, а не к объекту чека: список
	// перезапрашивается раз в семь секунд и отдаёт новый объект на ту же строку.
	// Завязавшись на него, панель дёргала бы кассу тремя лишними запросами на
	// каждое автообновление и мигала бы пустотой. Шапка при этом всё равно
	// берётся из свежей строки — устаревшей суммы тут не будет.
	const chequeId = cheque?.id ?? null
	const cloudId = cheque?.cloud_id ?? null

	const [detail, setDetail] = useState(EMPTY_DETAIL)
	const [loading, setLoading] = useState(false)
	const [showRaw, setShowRaw] = useState(false)
	const [showLogs, setShowLogs] = useState(false)

	const load = useCallback(async () => {
		if (!chequeId || !schema) return

		setLoading(true)
		setDetail(EMPTY_DETAIL)

		const result = await query(chequeDetailQuery(schema, chequeId, cloudId))
		const row = result.rows?.[0] || {}

		setDetail({
			// json_agg формально порядок строк подзапроса не гарантирует,
			// поэтому сортируем здесь: в истории хронология — это смысл.
			'products': sortById(parseJson(row.products)),
			'payments': sortById(parseJson(row.payments)),
			'statuses': sortById(parseJson(row.statuses)),
			'logs': sortById(parseJson(row.logs)),
			'error': result.error,
		})
		setLoading(false)
	}, [query, schema, chequeId, cloudId])

	// refreshToken в зависимостях — чтобы кнопка «Обновить» над списком
	// перечитывала и открытый справа чек, а не только строки слева.
	useEffect(() => {
		if (chequeId) load()
		else setDetail(EMPTY_DETAIL)
	}, [chequeId, refreshToken, load])

	if (!cheque) {
		return (
			<div className="card">
				<div className="card-body text-center text-muted">
					Выберите чек слева.
				</div>
			</div>
		)
	}

	const externalInfo = parseJson(cheque.external_info)
	const phone = cheque.client_phone || externalInfo?.phoneNumber

	// Отдельной колонки с суммой обслуживания в чеке нет, а умножать сумму на процент
	// нельзя — обслуживание берётся не со всех позиций. Поэтому выводим его из итога,
	// который касса и так посчитала: позиции + обслуживание + доставка + наценка − скидка.
	const serviceAmount = Number(cheque.total_amount_service || 0)
		- Number(cheque.total_amount || 0)
		- Number(cheque.delivery_price || 0)
		- Number(cheque.surcharge || 0)
		+ Number(cheque.discount || 0)

	// Касса пишет тип оплаты в строки cheques_payments, а cheques.payment_type_id
	// у оплаченного чека часто пустой — тогда собираем типы из строк оплат.
	const paymentTypeLabel = cheque.payment_type_name
		|| [...new Set(detail.payments.map((item) => item.payment_type_name).filter(hasValue))].join(', ')
		|| (hasValue(cheque.payment_type_id) ? `тип ${cheque.payment_type_id}` : '—')

	return (
		<div className="card">
			<div className="card-body">
				{/* Порядок строк — как на правой панели кассы. Номера чека и локального id
				    в шапке нет намеренно: № виден в списке слева, id — в «строке целиком» */}
				<div style={{ 'lineHeight': '20px' }}>
					<Row label="Чек ID" value={cheque.cloud_id} />
					{!cheque.cloud_id &&
						<div className="text-warning">Чека нет в облаке — создан на кассе</div>
					}
					<Row label="Сервис" value={cheque.external_name} />
					<Row label="Сервис ID" value={cheque.external_id} />
					<Row label="Клиент" value={cheque.client_name} />
					<Row label="Оператор" value={cheque.operator_name} />
					<Row label="Курьер" value={cheque.courier_name} />
					<Row label="Адрес" value={cheque.client_address} />

					{(hasValue(cheque.apartment) || hasValue(cheque.floor)) &&
						<div className="d-flex gap-4">
							<Row label="Квартира" value={cheque.apartment} />
							<Row label="Этаж" value={cheque.floor} />
						</div>
					}
					{(hasValue(cheque.entrance) || hasValue(cheque.intercom)) &&
						<div className="d-flex gap-4">
							<Row label="Подъезд" value={cheque.entrance} />
							<Row label="Домофон" value={cheque.intercom} />
						</div>
					}

					<Row label="Ориентир" value={cheque.navigate} />
					<Row label="Примечание" value={cheque.note || cheque.comment} />
					<Row label="Приборы" value={cheque.eating_kit} />

					{phone &&
						<div className="fz18 mt-1"><b>{regexPhoneNumber(phone)}</b></div>
					}
				</div>

				{(hasValue(cheque.payment_type_name) || Number(cheque.paid) > 0) &&
					<div className="d-flex justify-content-between border-top mt-2 pt-2">
						<div>
							<span>Оплата</span>
							{Number(cheque.paid) > 0 && <div>Оплачено</div>}
						</div>
						<div className="text-end">
							<div>{paymentTypeLabel}</div>
							{Number(cheque.paid) > 0 && <div>{formatMoney(cheque.paid)}</div>}
						</div>
					</div>
				}

				<div className="border-top mt-2 pt-2">
					<b>Состав заказа</b>
					{loading && <span className="text-muted fz12 ms-2">загрузка…</span>}
				</div>

				{detail.error &&
					<div className="alert alert-danger py-1 mt-2">{detail.error}</div>
				}

				<table className="table table-borderless table-sm mb-0">
					<tbody>
						{detail.products.map((item) => (
							<tr key={item.id}
								className={Number(item.status) === -1 ? 'text-decoration-line-through text-muted' : ''}>
								<td style={{ 'width': '50px' }}>x{renderCell(item.quantity)}</td>
								<td>
									{item.product_name || `product_id ${item.product_id}`}
									{item.comment && <div className="text-muted fz12">{item.comment}</div>}
								</td>
								<td className="text-end text-nowrap">{formatMoney(item.total_price)}</td>
							</tr>
						))}
						{detail.products.length === 0 && !loading &&
							<tr><td colSpan="3" className="text-center text-muted">Позиций нет</td></tr>
						}
					</tbody>
				</table>

				{/* Под составом заказа в кассе идут «Создано / Получено» — это НЕ
				    cheque_opened_at, а история статусов чека. Она ниже, теми же
				    подписями; открытие и изменение чека видны в «Истории чека». */}

				<div className="border-top mt-2 pt-2">
					<div className="d-flex justify-content-between">
						<span>Сумма</span>
						<span>{formatMoney(cheque.total_amount)}</span>
					</div>
					{(serviceAmount > 0 || Number(cheque.service_percentage) > 0) &&
						<div className="d-flex justify-content-between">
							<span>Обслуживание ({Number(cheque.service_percentage) || 0}%)</span>
							<span>{formatMoney(serviceAmount)}</span>
						</div>
					}
					{Number(cheque.surcharge) > 0 &&
						<div className="d-flex justify-content-between">
							<span>Наценка</span>
							<span>{formatMoney(cheque.surcharge)}</span>
						</div>
					}
					{(Number(cheque.delivery_price) > 0 || Number(cheque.order_type) === 3) &&
						<div className="d-flex justify-content-between">
							<span>Доставка</span>
							<span>{formatMoney(cheque.delivery_price || 0)}</span>
						</div>
					}
					<div className="d-flex justify-content-between">
						<span>Скидка</span>
						<span className={Number(cheque.discount) > 0 ? 'text-danger' : ''}>
							{formatMoney(cheque.discount || 0)}
						</span>
					</div>
					<div className="d-flex justify-content-between fz18">
						<b>К оплате</b>
						<b>{formatMoney(cheque.total_amount_service)}</b>
					</div>
				</div>


				{detail.payments.length > 0 &&
					<div className="border-top mt-2 pt-2">
						<b>Оплаты</b>
						<table className="table table-sm table-borderless mb-0">
							<tbody>
								{detail.payments.map((item) => (
									<tr key={item.id}>
										<td>{item.payment_type_name || renderCell(item.payment_type_id)}</td>
										<td className="text-end">{formatMoney(item.amount)}</td>
										<td className="text-end text-muted fz12">
											{formatPosDate(item.created_at, 'dd.MM HH:mm')}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				}

				<div className="border-top mt-2 pt-2">
					<b>Статусы заказа</b>
					<table className="table table-sm table-borderless mb-0">
						<tbody>
							{detail.statuses.map((item) => (
								<tr key={item.id}>
									<td style={{ 'width': '130px' }}>
										<b>{CHEQUE_STATUS_STEP_NAMES[String(item.status)] || `Статус ${item.status}`}</b>
									</td>
									<td className="text-nowrap">
										{formatPosDate(item.created_at, 'dd.MM.yyyy HH:mm:ss')}
									</td>
									<td className="text-end text-muted fz12">{renderCell(item.user_login)}</td>
								</tr>
							))}
							{detail.statuses.length === 0 &&
								<tr>
									<td className="text-muted">
										{cheque.cloud_id
											? 'Записей нет — заказ по статусам не проводили'
											: 'Статусы касса ведёт по cloud_id, а его у чека нет'}
									</td>
								</tr>
							}
						</tbody>
					</table>
				</div>

				{/* История — отдельной модалкой, как в кассе: в колонке шириной
				    в треть экрана таблица из четырёх колонок нечитаема. */}
				<div className="border-top mt-2 pt-2 d-flex gap-2 flex-wrap">
					<button type="button" className="btn btn-sm btn-outline-primary"
						disabled={loading}
						onClick={() => load()}>
						{loading ? '…' : 'Обновить'}
					</button>
					<button type="button" className="btn btn-sm btn-outline-primary"
						onClick={() => setShowLogs(true)}>
						История чека
						{detail.logs.length > 0 &&
							<span className="badge bg-primary ms-1">{detail.logs.length}</span>
						}
					</button>
					<button type="button" className="btn btn-sm btn-outline-secondary"
						onClick={() => setShowRaw(!showRaw)}>
						{showRaw ? 'Скрыть' : 'Показать'} строку целиком
					</button>
				</div>

				{showRaw &&
					<pre className="bg-light p-2 mt-2 fz12" style={{ 'maxHeight': '300px', 'overflow': 'auto' }}>
						{prettyJson(cheque)}
					</pre>
				}
			</div>

			<ChequeLogsModal
				show={showLogs}
				onHide={() => setShowLogs(false)}
				cheque={cheque}
				logs={detail.logs}
				loading={loading} />
		</div>
	)
}

export default ChequePanel
